import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Tabs, Button, message, Switch, Space, Image, Typography, Tag, Row, Col, Card, Tooltip } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../../config';
import QuillEditor from '../common/QuillEditor';

const { Title: AntTitle } = Typography;

interface ProductEditProps {
  productId: number;
  hotelId: number;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const LANGUAGES = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'en-GB', label: 'English (UK)' },
  { code: 'nl-NL', label: 'Dutch' },
  { code: 'de-DE', label: 'German' },
  { code: 'es-ES', label: 'Spanish' },
  { code: 'fr-FR', label: 'French' },
];

const ProductEdit: React.FC<ProductEditProps> = ({
  productId,
  hotelId,
  visible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState<any>(null);
  const [images, setImages] = useState<any[]>([]);

  useEffect(() => {
    if (visible && productId) {
      loadProduct();
    }
  }, [visible, productId]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/products/${productId}`);
      const productData = response.data;
      setProduct(productData);

      // Set form values
      form.setFieldsValue({
        isActive: productData.isActive,
        ...LANGUAGES.reduce((acc, lang) => {
          acc[`name_${lang.code}`] = productData.names?.[lang.code] || '';
          acc[`shortName_${lang.code}`] = productData.shortNames?.[lang.code] || '';
          acc[`externalName_${lang.code}`] = productData.externalNames?.[lang.code] || '';
          acc[`description_${lang.code}`] = productData.descriptions?.[lang.code] || '';
          acc[`emName_${lang.code}`] = productData.emNames?.[lang.code] || '';
          acc[`emDescription_${lang.code}`] = productData.emDescriptions?.[lang.code] || '';
          return acc;
        }, {} as any),
      });

      // Load product images
      if (productData.imageIds && productData.imageIds.length > 0) {
        loadImages(productData.imageIds);
      }
    } catch (error) {
      message.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const loadImages = async (imageIds: string[]) => {
    try {
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/images`);
      const productImages = response.data.filter((img: any) =>
        imageIds.includes(img.ImageId)
      );
      setImages(productImages);
    } catch (error) {
      console.error('Failed to load images');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Build multi-language objects
      const names: Record<string, string> = {};
      const shortNames: Record<string, string> = {};
      const externalNames: Record<string, string> = {};
      const descriptions: Record<string, string> = {};
      const emNames: Record<string, string> = {};
      const emDescriptions: Record<string, string> = {};

      LANGUAGES.forEach((lang) => {
        if (values[`name_${lang.code}`]) {
          names[lang.code] = values[`name_${lang.code}`];
        }
        if (values[`shortName_${lang.code}`]) {
          shortNames[lang.code] = values[`shortName_${lang.code}`];
        }
        if (values[`externalName_${lang.code}`]) {
          externalNames[lang.code] = values[`externalName_${lang.code}`];
        }
        if (values[`description_${lang.code}`]) {
          descriptions[lang.code] = values[`description_${lang.code}`];
        }
        if (values[`emName_${lang.code}`]) {
          emNames[lang.code] = values[`emName_${lang.code}`];
        }
        if (values[`emDescription_${lang.code}`]) {
          emDescriptions[lang.code] = values[`emDescription_${lang.code}`];
        }
      });

      await axios.put(`${API_URL}/products/${productId}`, {
        names,
        shortNames,
        externalNames,
        descriptions,
        emNames,
        emDescriptions,
        isActive: values.isActive,
      });

      message.success('Product updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToEm = (langCode: string) => {
    const values = form.getFieldsValue();
    const name = values[`name_${langCode}`];
    const description = values[`description_${langCode}`];

    if (!name && !description) {
      message.warning('No original content to copy');
      return;
    }

    form.setFieldsValue({
      [`emName_${langCode}`]: name || '',
      [`emDescription_${langCode}`]: description || '',
    });

    message.success('Original content copied to EM fields');
  };

  const languageTabs = LANGUAGES.map((lang) => ({
    key: lang.code,
    label: lang.label,
    children: (
      <Row gutter={24} align="top">
        {/* Left side: Original Content */}
        <Col span={11}>
          <Card
            title="Original Content (From PMS)"
            size="small"
            headStyle={{ background: '#fafafa', fontWeight: 600 }}
            bodyStyle={{ minHeight: 400 }}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Form.Item
                label="Name"
                name={`name_${lang.code}`}
              >
                <Input placeholder={`Product name in ${lang.label}`} />
              </Form.Item>

              <Form.Item
                label="Short Name"
                name={`shortName_${lang.code}`}
              >
                <Input placeholder={`Short name in ${lang.label}`} />
              </Form.Item>

              <Form.Item
                label="External Name"
                name={`externalName_${lang.code}`}
              >
                <Input placeholder={`External name in ${lang.label}`} />
              </Form.Item>

              <Form.Item
                label="Description"
                name={`description_${lang.code}`}
              >
                <QuillEditor placeholder={`Description in ${lang.label}`} />
              </Form.Item>
            </Space>
          </Card>
        </Col>

        {/* Middle: Copy button */}
        <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 100 }}>
          <Tooltip title="Copy original content to EM fields">
            <Button
              type="primary"
              shape="circle"
              icon={<ArrowRightOutlined />}
              size="large"
              onClick={() => handleCopyToEm(lang.code)}
              style={{ boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)' }}
            />
          </Tooltip>
        </Col>

        {/* Right side: EM Content */}
        <Col span={11}>
          <Card
            title="EM Content"
            size="small"
            headStyle={{ background: '#f0f9ff', fontWeight: 600, color: '#1890ff' }}
            bodyStyle={{ minHeight: 400 }}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Form.Item
                label="EM Name"
                name={`emName_${lang.code}`}
              >
                <Input placeholder={`EM name in ${lang.label}`} />
              </Form.Item>

              <Form.Item
                label="EM Description"
                name={`emDescription_${lang.code}`}
              >
                <QuillEditor placeholder={`EM description in ${lang.label}`} />
              </Form.Item>
            </Space>
          </Card>
        </Col>
      </Row>
    ),
  }));

  return (
    <Modal
      title="Edit Product"
      open={visible}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save"
      confirmLoading={saving}
      width={1200}
      destroyOnClose
      style={{ top: 20 }}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 24 }}
      >
        <Form.Item
          label="Active"
          name="isActive"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        {product && (
          <div style={{ marginTop: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
            <Space direction="vertical">
              <div><strong>Product ID:</strong> {product.mewsId}</div>
              {product.price && (
                <div><strong>Price:</strong> {JSON.stringify(product.price)}</div>
              )}
              {product.classifications && (
                <div>
                  <strong>Classifications:</strong>
                  <Space wrap style={{ marginLeft: 8 }}>
                    {Object.entries(product.classifications).map(([key, value]: any) => (
                      <Tag key={key}>{key}: {value}</Tag>
                    ))}
                  </Space>
                </div>
              )}
            </Space>
          </div>
        )}

        <Tabs items={languageTabs} style={{ marginTop: 24 }} />

        {images.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <AntTitle level={5}>Product Images ({images.length})</AntTitle>
            <Image.PreviewGroup>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                {images.map((img) => (
                  <Image
                    key={img.ImageId}
                    src={img.Url}
                    alt="Product"
                    width={150}
                    height={150}
                    style={{ objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                  />
                ))}
              </div>
            </Image.PreviewGroup>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default ProductEdit;
